import React, {useState, useEffect, useContext, createContext, useMemo, useRef} from 'react';

import {getAllMenuItems, moveItem, removeItem as removeItemApi} from '../api/lists'
import { scheduledStatus } from  '@/helpers/List';

import { ListContext } from '@/context/ListContext';

//icons
import { IconLibrary } from  '@/interfaces/IconLibrary';
import Icon from  '@/components/elements/icons/Icons';

//components
import MainMenuContentTile from '../components/elements/lists/MainMenuContentTitle';
import ContentItemPublishedStatus from '../components/elements/lists/ContentItemPublishedStatus';
import ContentItemAudienceCount from '../components/elements/lists/ContentItemAudienceCount';

//interfaces
import { ListContributorViewOptions } from  '@/components/elements/lists/ViewToggle';
import { NotificationStatus } from  '@/interfaces/languages';
import { getPermissionString } from  '@/helpers/Content';
import { PaginationSettings } from  '@/interfaces/lists';




const MainMenuListContextInitialState: any = {
    menuItems: [],
    formatTableRows: () => {},
    showArchiveModal: false,
    setShowArchiveModal: () => {},
    contentExistsInMultipleLocations: false,
    showDisplayMainMenuTable: false,
    setShowDisplayMainMenuTable: () => {},
    viewOption: ListContributorViewOptions,
    setViewOption: (newViewOption: ListContributorViewOptions) => {},
    notificationDetails: {show: false, status: null, message: ""},
    setNotificationDetails: ({show: boolean, status: NotificationStatus, message: string}) => {},
    moreAvailable: false,
}



export const MainMenuListContext = createContext(MainMenuListContextInitialState)

const MainMenuListContextProvider = ({children}:{children: React.ReactNode}) => {
    const top = 20;
    const [menuItems, setMenuItems] = useState<any[]>([]);
    const [paginationSettings, setPaginationSettings] = useState<PaginationSettings>({hasPagination: false, currentPage: 1, totalPages:1, pageSize: 20, totalItems: 0});
    const {listId, appGuid, hasEditorRights, appId} = useContext(ListContext);
    const [viewOption, setViewOption] = useState<ListContributorViewOptions>(ListContributorViewOptions.AllItems)
    const [showArchiveModal, setShowArchiveModal] = useState<boolean>(false);
    const [selectedItemId, setSelectedItemId] = useState<number|null>(null);
    const [contentExistsInMultipleLocations, setContentExistsInMultipleLocations] = useState<boolean>(false);
    const [displayMainMenuTable, setDisplayMainMenuTable] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [notificationDetails, setNotificationDetails] = useState<{show: boolean, status: NotificationStatus, message: string}>({show: false, status: null, message: ""});
    const [skip, setSkip] = useState<number>(0);
    const [moreAvailable, setMoreAvailable] = useState<boolean>(false);
   
    /* ==================== SOME X-FRAMEWORK STUFF =================== */
    // update menu items function is a closure. It's called in jquery in lists.js
    // we need to use refs to ensure that the correct and most recent values are being used in that function
    const listIdRef = useRef<string>('');
    const appIdRef = useRef<string>('');
    const topRef = useRef<number>(20);
    const downloadContentReportUrl = `/a/${appGuid}/Content/DownloadPublishedContentReport`

    // whenever appguid, listid or sorttype change in context update the refs
    useEffect(() => {
        appIdRef.current = appId
        listIdRef.current = listId;
        topRef.current = menuItems.length;
    }, [appId, listId, menuItems.length, skip])

    /* ====================== END OF X-FRAMEWORK STUFF ===================== */ 

    useEffect(() => {
        (window as any).functions = {...(window as any).functions, refreshMainMenu: () => getMenuData('refresh')}
        return () => {delete (window as any)?.functions?.refreshMainMenu}
    },[appId, listId, menuItems.length]);
    
    useEffect(() => {
        listId && getMenuData('new')
    }, [listId, viewOption]);

    useEffect(()=> {
        setDisplayMainMenuTable(true);
    },[menuItems]);

    useEffect(() => {
        if (notificationDetails.show) {
            const timer = setTimeout(() => {
                setNotificationDetails({show: false, status:null, message: ""});
            }, 3000);

            return () => clearTimeout(timer);
        }
    }, [notificationDetails]);

    function getMoreMenuItems(){
        getMenuData('paginate');
    }

    async function getMenuData(type: 'paginate' | 'refresh' | 'new'): Promise<void> {
        if (!listIdRef.current && !listId){
            return
        }
        
        const viewMyItems : boolean = viewOption === ListContributorViewOptions.MyItems;
        const topValue: number = type === 'refresh' ? topRef.current + 1 : top;
        const skipValue: number = type === 'paginate' ? skip + top : 0

        getAllMenuItems(appId || appIdRef.current, listId || listIdRef.current, topValue, skipValue, viewMyItems)
        .then((data) => {
             if (type === 'paginate') {
                 setMenuItems((prevTableData) => [...(prevTableData || []), ...data.records]);
             } else {
                 setMenuItems(data.records) 
             }
             if(type !== 'refresh') {
                 setSkip(data.skip);
             }
             setPaginationSettings({
                hasPagination: false,
                currentPage: 0,
                totalPages: 0,
                pageSize: 0,
                totalItems: data.total
            })
             setMoreAvailable(data.moreAvailable);
        })
        .finally(() => {
            setIsLoading(false);
        });
    }

    async function moveMenuItem(itemId, newPosition) {
        const payload = {toPosition: newPosition.toString()}
        try {
            await moveItem(listId, itemId, payload);
        } catch (error) {
            setNotificationDetails({ show: true, status: NotificationStatus.error, message: "Unable to reorder content items, please try again."});
        }
        getMenuData('refresh');
    }

    async function onArchiveItemClick() {
        if (selectedItemId) {
            try {
                await removeItemApi(listId.toString(), selectedItemId.toString());
                setNotificationDetails({show: true, status: NotificationStatus.success, message: "Successfully archived item"});
            } catch (error) {
                setNotificationDetails({ show: true, status: NotificationStatus.error, message: "Unable to archive item, please try again."});
            }
            setSelectedItemId(null);
        }
        getMenuData('refresh')
        setShowArchiveModal(false);
    }

    function onOpenArchiveModal(item: any) {
        setSelectedItemId(item.id);
        setContentExistsInMultipleLocations(item.parentIdAndTitles.length > 1);
        setShowArchiveModal(true);
    }

    function formatTableRows(){
        return menuItems.map((item, idx) => ({
            content: [
                <MainMenuContentTile
                    key={`${item.id}-title`}
                    item={item}
                    listId={listId}
                    appGuid={appGuid}
                    isHome={idx === 0}
                    status={<ContentItemPublishedStatus item={item}/>}
                    archiveStatus={scheduledStatus(item.scheduledPublishDate, item.scheduledArchiveDate)}
                />, 
                <ContentItemAudienceCount key={`${item.id}-count`} count={item.groupUserCount} everyoneHasAccess={item.everyoneHasAccess}/>,
                getPermissionString(item.permission)],
            controls: hasEditorRights
                ? [{content: <span className='archive-wrapper'><Icon iconName={IconLibrary.ARCHIVE} sizeInPx='12' color='#D12A36' /><span>Archive</span></span>, onClick: ()=> onOpenArchiveModal(item)}]
                : undefined,
            dragAndDropAction: (newIndex) => moveMenuItem(item.id, newIndex)
        }))
    }

    const context = useMemo(() => ({menuItems, formatTableRows, setShowArchiveModal, showArchiveModal, onArchiveItemClick, contentExistsInMultipleLocations, displayMainMenuTable, viewOption, setViewOption, notificationDetails, setNotificationDetails, getMoreMenuItems, moreAvailable, isLoading, paginationSettings, downloadContentReportUrl}), [displayMainMenuTable, menuItems, showArchiveModal, notificationDetails, isLoading, hasEditorRights, appId])

    return (
        <MainMenuListContext.Provider value={context}>
            {children}
        </MainMenuListContext.Provider>
    )
}

export default MainMenuListContextProvider